/**
 * Customer-side Capacitor native bridge — runs only when wrapped as the
 * "EMA Kurer" customer app on iOS/Android.
 *
 * Provides:
 *   - Native push registration tied to a customer's email (so they get
 *     booking-confirmation, pickup-on-the-way, and delivered notifications)
 *   - Deep linking from a tapped notification → /track or /betaling/:id
 *
 * Web/PWA users continue using the existing service-worker Web-Push flow.
 */
import { Capacitor } from "@capacitor/core";
import { api } from "./api";

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform();

let _registered = false;

const PENDING_KEY = "ema-customer-pending-native-token";
const EMAIL_KEY = "ema-customer-email";

/**
 * Persist the customer email locally so we can re-bind a token on reinstall.
 */
export function rememberCustomerEmail(email) {
  try {
    if (email) localStorage.setItem(EMAIL_KEY, String(email).toLowerCase());
  } catch (e) { console.warn("rememberCustomerEmail failed:", e?.message); }
}
export function getRememberedEmail() {
  try { return localStorage.getItem(EMAIL_KEY) || ""; } catch (e) { console.warn("getRememberedEmail failed:", e?.message); return ""; }
}

async function _sendTokenToBackend(token, email) {
  if (!email) return false;
  try {
    await api.post("/customer/push/native-register", {
      token,
      platform: platform(),
      email,
    });
    return true;
  } catch (e) {
    console.warn("customer push register failed:", e);
    return false;
  }
}

/**
 * Initialize push notifications for the customer-app shell.
 * Idempotent. Pass the customer's email (from booking flow or remembered storage).
 */
export async function initCustomerNativePush(emailOrGetter) {
  if (!isNative() || _registered) return { ok: false, reason: "not-native-or-already" };
  const email = (typeof emailOrGetter === "function" ? emailOrGetter() : emailOrGetter) || getRememberedEmail();
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    const perm = await PushNotifications.checkPermissions();
    let receive = perm.receive;
    if (receive === "prompt" || receive === "prompt-with-rationale") {
      const req = await PushNotifications.requestPermissions();
      receive = req.receive;
    }
    if (receive !== "granted") return { ok: false, reason: "denied" };

    await PushNotifications.register();

    const tokenPromise = new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error("Push registration timeout")), 15000);
      PushNotifications.addListener("registration", (token) => {
        clearTimeout(t);
        resolve(token.value);
      });
      PushNotifications.addListener("registrationError", (err) => {
        clearTimeout(t);
        reject(err);
      });
    });
    const token = await tokenPromise;

    if (email) {
      const ok = await _sendTokenToBackend(token, email);
      if (!ok) {
        try { localStorage.setItem(PENDING_KEY, JSON.stringify({ token, platform: platform(), email })); } catch (e) { console.warn("Pending token cache failed:", e?.message); }
      }
    } else {
      try { localStorage.setItem(PENDING_KEY, JSON.stringify({ token, platform: platform() })); } catch (e) { console.warn("Pending token cache failed:", e?.message); }
    }

    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const data = action.notification?.data || {};
      if (data.booking_id && data.url) {
        window.location.href = data.url;
      } else if (data.booking_number) {
        window.location.href = `/track?b=${encodeURIComponent(data.booking_number)}${data.email ? `&e=${encodeURIComponent(data.email)}` : ""}`;
      } else {
        window.location.href = "/";
      }
    });

    _registered = true;
    return { ok: true, token };
  } catch (err) {
    console.error("initCustomerNativePush failed:", err);
    return { ok: false, reason: err?.message || String(err) };
  }
}

/**
 * Flush any token captured before the customer email was known
 * (e.g., user installed app, browsed for a while, then booked).
 * Call this right after a successful booking is created.
 */
export async function flushPendingCustomerToken(email) {
  if (!isNative()) return;
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw);
    const e = email || obj.email || getRememberedEmail();
    if (!e) return;
    const ok = await _sendTokenToBackend(obj.token, e);
    if (ok) localStorage.removeItem(PENDING_KEY);
  } catch (e) {
    console.warn("flushPendingCustomerToken failed:", e);
  }
}
